import { Group, Text } from '@mantine/core';
import { ContentCard } from '../../../components/common/ContentCard';
import { Eye } from 'react-feather';

// MED-1059 — match the main-column cards' icon + title header (AC1).
const TITLE_COLOR = '#1A1A2E';
import type { PostSessionSidebarLeaderVisibility } from '../../../services/api/participantPostSession';

interface Props {
  visibility: PostSessionSidebarLeaderVisibility;
}

/**
 * "What [leader] sees" sidebar card (MED-806 AC17; updated by MED-989 AC7).
 *
 * Per MED-989, the participant's feedback answers and rating scales are Medley
 * measurement data and are NOT shared directly with the leader — Medley relays
 * them in follow-up conversations. The only feedback the leader sees is the
 * forward-looking "next session" intention (what_for_next), plus the commitment
 * when Step 3 (Finalize Commitment) is enabled for the cohort.
 */
export function LeaderVisibilitySidebarCard({ visibility }: Props) {
  // MED-1170 — neutral, lowercase fallback so the interpolation reads correctly
  // in both the title ("What your session leader sees") and the body ("…with
  // your session leader…"), and is accurate for peer-led cohorts (no "coach").
  // MED-1059 AC7 kept the fallback lowercase; this keeps that and fixes the term.
  const leader = visibility.leader_first_name || 'your session leader';
  const copy = visibility.commitment_tracking_enabled
    ? `What you want from the next session, and your commitment. Your ratings and feedback answers go to Medley — not shared directly with ${leader} — and your private session notes stay yours alone.`
    : `What you want from the next session. Your ratings and feedback answers go to Medley — not shared directly with ${leader} — and your private session notes stay yours alone.`;

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap={6}
      data-testid="sidebar-leader-visibility"
      style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
      header={
        <Group gap={8} align="center" wrap="nowrap">
          <Eye
            size={18}
            color={TITLE_COLOR}
            aria-hidden="true"
            style={{ marginTop: -4 }}
          />
          <Text
            fz={15}
            fw={700}
            c={TITLE_COLOR}
            data-testid="sidebar-leader-visibility-title"
          >
            What {leader} sees
          </Text>
        </Group>
      }
    >
      <Text size="sm" c="dimmed" data-testid="sidebar-leader-visibility-copy">
        {copy}
      </Text>
    </ContentCard>
  );
}
